import { LoadSpinner } from '@calizahq/react-components-v2'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import { BottomSheet, FAB, Tab } from '@/components'
import { TabNavigation } from '@/components/TabNavigation/TabNavigation'
import { CreateJobForm, CreateTicketForm, HomeBlockForm } from '@/forms'
import { ReportIssueForm } from '@/forms/ReportIssue/ReportIssueForm'
import { useReservationHome, useToggle } from '@/hooks'
import { AccessLevel, useAccessLevel } from '@/hooks/useAccessLevel'
import { useHomeQualityCheck } from '@/hooks/useHomeQualityChecks'
import { useTimeline } from '@/hooks/useTimeline'

import { HomeAccess } from './HomeAccess/HomeAccess'
import { HomeDetail } from './HomeDetail/HomeDetail'
import { HomeHistory } from './HomeHistory/HomeHistory'
import { HomeQualityChecks } from './HomeQualityChecks/HomeQualityChecks'
import { HomeTickets } from './HomeTickets/HomeTickets'

export const Home = () => {
  const { homeId: homeIdString } = useParams<{ homeId: string }>()
  const homeId = Number(homeIdString)
  const [selectedOption, setSelectedOption] = useState<SelectedOptionType>()
  const { isShown, toggle } = useToggle()
  const { isShown: modalShown, toggle: modalToggle } = useToggle()
  const { refetch: refetchTimeline } = useTimeline({ id: homeId })
  const [homeQualityCheckId, setHomeQualityCheckId] = useState<number>()

  const { refetch: refetchHomeQualityCheck } = useHomeQualityCheck({ homeQualityCheckId })
  const { isLoading, data, refetch: refetchHome } = useReservationHome({ homeId })

  const { accessGranted } = useAccessLevel()
  const { HOME_BLOCK_PERMITTED } = AccessLevel

  type SelectedOptionType = (typeof statusOptions)[0]

  const statusOptions: { modalHeader?: string; name: string; value: OptionsValue }[] = [
    { name: 'Create Ticket', value: OptionsValue.CREATE_TICKET, modalHeader: undefined },
    { name: 'Create Host Job', value: OptionsValue.CREATE_JOB, modalHeader: 'Create Host Job' },
    { name: 'Report Home Issue', value: OptionsValue.REPORT_ISSUE, modalHeader: 'Report New Home Issue' },
  ]

  if (accessGranted([HOME_BLOCK_PERMITTED])) {
    statusOptions.push({
      name: 'Add Block Reason',
      value: OptionsValue.HOME_BLOCK,
      modalHeader: 'Home Block Reason',
    })
  }

  const showActionModal = (option: SelectedOptionType) => {
    toggle() // Close Fob Options
    setSelectedOption(option)
    modalToggle() // Show Modal
  }

  const onCreateJobSuccess = () => {
    refetchTimeline()
    modalToggle()
  }

  const onReportIssueSuccess = () => {
    refetchTimeline()
    // We only need to refetch the Home Quality Check if the Issue was Reported from the context of an Inspection.
    if (homeQualityCheckId) {
      refetchHomeQualityCheck()
    }
  }

  return (
    <div className="flex min-h-140 w-full flex-col p-4 md:p-8">
      {isLoading ? (
        <div className="flex h-120 w-full items-center justify-center">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <>
          <div className="flex w-full flex-col">
            <h2 className=" font-medium text-header-md md:text-header-lg">{data?.home.searchDocument}</h2>
            <TabNavigation>
              <Tab clickName="details" title="Details">
                <HomeDetail homeId={homeId} />
              </Tab>
              <Tab clickName="homeQualityChecks" title="Quality Checks">
                <HomeQualityChecks
                  homeId={homeId}
                  homeQualityCheckId={homeQualityCheckId}
                  refetchOnSuccess={refetchTimeline}
                  setHomeQualityCheckId={setHomeQualityCheckId}
                />
              </Tab>
              <Tab clickName="access" title="Access">
                <HomeAccess homeId={homeId} />
              </Tab>
              <Tab title="Tickets">
                <HomeTickets homeId={homeId} />
              </Tab>
              <Tab title="History">
                <HomeHistory homeId={homeId} />
              </Tab>
            </TabNavigation>
          </div>
          <FAB isShown={isShown} toggle={toggle}>
            {statusOptions.map((option, index) => {
              return (
                <button
                  className={`option w-full whitespace-nowrap py-3 text-left font-medium ${
                    index !== 0 ? 'border-t border-secondary' : ''
                  }`}
                  key={index}
                  onClick={() => showActionModal(option)}
                >
                  {option.name}
                </button>
              )
            })}
          </FAB>
          <BottomSheet
            header={selectedOption?.modalHeader || undefined}
            isVisible={modalShown}
            mobileFullHeight={selectedOption?.value === OptionsValue.CREATE_TICKET}
            onClose={modalToggle}
          >
            <div className="px-6 ">
              {selectedOption?.value === OptionsValue.HOME_BLOCK && (
                <HomeBlockForm homeId={homeId} refetchOnSuccess={refetchHome} toggle={modalToggle} />
              )}
              {selectedOption?.value === OptionsValue.CREATE_JOB && (
                <CreateJobForm homeId={homeId} onSuccess={onCreateJobSuccess} />
              )}
            </div>
            {selectedOption?.value === OptionsValue.CREATE_TICKET && homeId && (
              <CreateTicketForm homeId={homeId} toggle={modalToggle} />
            )}
            {selectedOption?.value === OptionsValue.REPORT_ISSUE && homeId && (
              <ReportIssueForm
                homeId={homeId}
                homeQualityCheckId={homeQualityCheckId}
                refetchOnSuccess={onReportIssueSuccess}
                toggle={modalToggle}
              />
            )}
          </BottomSheet>
        </>
      )}
    </div>
  )
}

enum OptionsValue {
  CREATE_JOB = 'create_job',
  CREATE_TICKET = 'create_ticket',
  HOME_BLOCK = 'home_block',
  REPORT_ISSUE = 'report_issue',
}
